import React, { useEffect, useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { User, Shield, School, Database, Calendar, Check, Sparkles, Edit2, Save, X, AlertCircle, Eye, Heart, GraduationCap, Copy, CheckCircle2, Link as LinkIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { ProgressBar } from '../../components/ui/Progress';
import { UserStats } from '../../app/types';
import { AccountRole, AccountsState, isLearnerAccount, isTeacherAccount } from '../types';
import { getAccountRoleLabel } from '../logic/uiHelpers';
import { GroupId, StartingPeriod, STARTING_PERIODS } from '../../config/systemConfig';
import { resolvePhaseByDate, getAutoPhaseLabel } from '../../mastery';
import { getProgressToNextLevel } from '../../config/constants';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
interface ProfileSectionProps {
  stats: UserStats;
  displayName: string;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  manualPeriod?: StartingPeriod | null;
  onUpdateName?: (newName: string) => void;
  accountRole?: AccountRole;
  isViewingLearner?: boolean;
  accounts?: AccountsState;
  viewingLearnerId?: string | null;
  hasMasteryData?: boolean;
  onResetProgress?: () => void;
}
export function ProfileSection({
  stats,
  displayName,
  selectedGroup,
  startingPeriod,
  onSetStartingPeriod,
  manualPeriod,
  onUpdateName,
  accountRole = 'learner',
  isViewingLearner = false,
  accounts,
  viewingLearnerId,
  hasMasteryData = false,
  onResetProgress
}: ProfileSectionProps) {
  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(displayName);
  useEffect(() => {
    setEditedName(displayName);
  }, [displayName]);
  const handleSaveName = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== displayName && onUpdateName) {
      onUpdateName(trimmedName);
      setIsEditingName(false);
    } else {
      setEditedName(displayName);
      setIsEditingName(false);
    }
  };
  const handleCancelEdit = () => {
    setEditedName(displayName);
    setIsEditingName(false);
  };
  // Role display label — centralized in accountUIHelpers (C5 fix)
  const roleLabel = getAccountRoleLabel(accountRole);
  // Determine if we should show learning controls (XP, Level, Period)
  // Show if: Role is learner OR (Role is parent/teacher AND viewing a learner)
  const showLearningControls = accountRole === 'learner' || isViewingLearner;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: 'easeOut'
      }
    }
  };
  // Get linked parents and teachers for the current learner
  const linkedInfo = useMemo(() => {
    if (!accounts || accountRole !== 'learner') return null;
    // Use viewingLearnerId when available (parent/teacher viewing a learner),
    // otherwise fall back to activeAccountId (learner viewing own profile)
    const learnerId = isViewingLearner && viewingLearnerId ? viewingLearnerId : accounts.activeAccountId;
    if (!learnerId) return null;
    const learnerAccount = accounts.accountsById[learnerId];
    if (!learnerAccount || !isLearnerAccount(learnerAccount)) return null;
    // Get linked parents
    const parentIds = accounts.relationships.learnerToParents[learnerId] || [];
    const parents = parentIds.map(id => accounts.accountsById[id]).filter(Boolean).map(a => ({
      name: a.displayName,
      role: 'Ouder' as const
    }));
    // Get linked teachers (iterate teacherToLearners — no reverse mapping yet)
    const teachers: {
      name: string;
      role: 'Docent';
      className?: string;
    }[] = [];
    Object.entries(accounts.relationships.teacherToLearners).forEach(([teacherId, learnerIds]) => {
      if (learnerIds.includes(learnerId)) {
        const teacher = accounts.accountsById[teacherId];
        if (teacher && isTeacherAccount(teacher)) {
          // Find classroom name from top-level registry
          const teacherClassroom = Object.values(accounts.classrooms ?? {}).find(c => c.teacherId === teacherId && c.learnerIds.includes(learnerId));
          teachers.push({
            name: teacher.displayName,
            role: 'Docent',
            className: teacherClassroom?.name
          });
        }
      }
    });
    return {
      parents,
      teachers,
      linkCode: learnerAccount.linkCode,
      learnerId
    };
  }, [accounts, accountRole, isViewingLearner, viewingLearnerId]);
  // Copy link code state
  const [codeCopied, setCodeCopied] = useState(false);
  const handleCopyCode = () => {
    if (linkedInfo?.linkCode) {
      navigator.clipboard.writeText(linkedInfo.linkCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };
  return <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="show" data-id="element-191">
      {/* Banner for Parent/Teacher viewing Learner */}
      {isViewingLearner && <motion.div variants={itemVariants} data-id="element-192">
          <Card className="p-4 bg-emerald-50 border-emerald-200 border-2" data-id="element-193">
            <div className="flex items-center gap-3 text-emerald-800" data-id="element-194">
              <Edit2 className="w-5 h-5" data-id="element-195" />
              <p className="font-bold" data-id="element-196">
                Je bekijkt en bewerkt nu de instellingen van{' '}
                <span className="underline" data-id="element-197">{displayName}</span>.
              </p>
            </div>
          </Card>
        </motion.div>}

      {/* Banner for Parent/Teacher NOT viewing Learner */}
      {!showLearningControls && <motion.div variants={itemVariants} data-id="element-198">
          <Card className="p-6 bg-orange-50 border-orange-200 border-2" data-id="element-199">
            <div className="flex items-start gap-4" data-id="element-200">
              <div className="bg-white p-2 rounded-full shadow-sm" data-id="element-201">
                <User className="w-6 h-6 text-orange-600" data-id="element-202" />
              </div>
              <div data-id="element-203">
                <h3 className="font-bold text-orange-900 text-lg mb-1" data-id="element-204">
                  Jouw Profiel
                </h3>
                <p className="text-orange-700 mb-2" data-id="element-205">
                  Je bent ingelogd als <strong data-id="element-206">{roleLabel}</strong>. Hier kun je
                  je eigen naam aanpassen.
                </p>
                <div className="flex items-center gap-2 text-sm text-orange-600 font-medium" data-id="element-207">
                  <AlertCircle className="w-4 h-4" data-id="element-208" />
                  Selecteer een leerling in het portaal om hun instellingen te
                  wijzigen.
                </div>
              </div>
            </div>
          </Card>
        </motion.div>}

      {/* Main Profile Card */}
      <motion.div variants={itemVariants} data-id="element-209">
        <Card className="p-10 md:p-12" data-id="element-210">
          <div className="flex flex-col sm:flex-row items-center gap-8" data-id="element-211">
            {/* Avatar */}
            <div className="relative flex-shrink-0" data-id="element-212">
              <div className={`
                w-28 h-28 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg shadow-orange-200/50
                ${showLearningControls ? 'bg-gradient-to-br from-orange-400 to-amber-500' : 'bg-gradient-to-br from-slate-400 to-slate-600'}
              `} data-id="element-213">
                {displayName.charAt(0).toUpperCase()}
              </div>
              {showLearningControls && <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm" data-id="element-214">
                  <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-215">
                    Lvl {stats.level}
                  </div>
                </div>}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left" data-id="element-216">
              {/* Name with Edit */}
              {isEditingName ? <div className="flex items-center gap-3 mb-3" data-id="element-217">
                  <Input type="text" value={editedName} onChange={e => setEditedName(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEdit();
              }} className="text-3xl font-bold text-slate-900 border-2 border-orange-300 rounded-lg px-4 py-2 focus:border-orange-500 min-h-[56px]" autoFocus maxLength={30} fullWidth={false} data-id="element-218" />
                  <button onClick={handleSaveName} className="p-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-emerald-300" title="Opslaan" data-id="element-219">
                    <Save className="w-5 h-5" data-id="element-220" />
                  </button>
                  <button onClick={handleCancelEdit} className="p-3 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors min-h-[48px] min-w-[48px] focus:outline-none focus:ring-2 focus:ring-slate-300" title="Annuleren" data-id="element-221">
                    <X className="w-5 h-5" data-id="element-222" />
                  </button>
                </div> : <div className="flex items-center gap-4 mb-3 justify-center sm:justify-start" data-id="element-223">
                  <h2 className="text-3xl font-black text-slate-900" data-id="element-224">
                    {displayName}
                  </h2>
                  {onUpdateName && <button onClick={() => setIsEditingName(true)} className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors min-h-[40px] min-w-[40px] focus:outline-none focus:ring-2 focus:ring-orange-300" title="Naam wijzigen" data-id="element-225">
                      <Edit2 className="w-5 h-5" data-id="element-226" />
                    </button>}
                </div>}

              <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-3" data-id="element-227">
                {showLearningControls && <Badge variant="info" icon={<School className="w-4 h-4" data-id="element-229" />} size="lg" data-id="element-228">
                    Groep {selectedGroup}
                  </Badge>}
                <Badge variant="default" icon={<Shield className="w-4 h-4" data-id="element-231" />} size="lg" data-id="element-230">
                  {roleLabel}
                </Badge>
              </div>

              {/* XP Progress - Only for Learners */}
              {showLearningControls && <Card className="mt-6 max-w-xs" data-id="element-232">
                  <div className="flex items-center justify-between mb-2" data-id="element-233">
                    <span className="text-sm font-semibold text-slate-600" data-id="element-234">
                      Level {stats.level}
                    </span>
                    <span className="text-sm font-bold text-orange-600" data-id="element-235">
                      {stats.xp} XP
                    </span>
                  </div>
                  <ProgressBar progress={getProgressToNextLevel(stats.xp).percent} color="orange" size="md" animated data-id="element-236" />
                  <p className="text-xs text-slate-500 mt-2 text-center" data-id="element-237">
                    Nog{' '}
                    {getProgressToNextLevel(stats.xp).needed - getProgressToNextLevel(stats.xp).current}{' '}
                    XP tot Level {stats.level + 1}
                  </p>
                </Card>}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Koppelcode Section — Only for Learners */}
      {showLearningControls && linkedInfo?.linkCode && <motion.div variants={itemVariants} data-id="element-238">
          <Card className="p-8" data-id="element-239">
            <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-xl" data-id="element-240">
              <LinkIcon className="w-6 h-6 text-orange-500" data-id="element-241" />
              Mijn Koppelcode
            </h3>
            <p className="text-base text-slate-600 mb-4 font-semibold" data-id="element-242">
              Deel deze code met je ouder(s) zodat zij je voortgang kunnen
              volgen.
            </p>
            <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800 mb-5" data-id="element-243">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" data-id="element-244" />
              <div data-id="element-245">
                <span className="font-bold" data-id="element-246">Wat kan een ouder zien?</span> Je
                scores, welke vakken je oefent en hoe lang. Ze kunnen{' '}
                <span className="font-bold" data-id="element-247">niet</span> je wachtwoord zien of je
                instellingen wijzigen.
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-248">
              <div className="bg-orange-50 border-2 border-orange-200 px-6 py-3 rounded-2xl font-mono text-2xl font-black tracking-widest text-orange-900" data-id="element-249">
                {linkedInfo.linkCode}
              </div>
              <button onClick={handleCopyCode} className="p-3 bg-orange-50 border-2 border-orange-100 rounded-xl text-orange-600 hover:bg-orange-100 hover:border-orange-200 transition-all" title="Kopieer code" data-id="element-250">
                {codeCopied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-251" /> : <Copy className="w-5 h-5" data-id="element-252" />}
              </button>
              {codeCopied && <span className="text-sm font-bold text-emerald-600" data-id="element-253">
                  Gekopieerd!
                </span>}
            </div>
          </Card>
        </motion.div>}

      {/* Wie kan mijn voortgang zien? — Only for Learners */}
      {showLearningControls && linkedInfo && (linkedInfo.parents.length > 0 || linkedInfo.teachers.length > 0) && <motion.div variants={itemVariants} data-id="element-254">
            <Card className="p-8" data-id="element-255">
              <h3 className="font-black text-slate-900 mb-2 flex items-center gap-3 text-xl" data-id="element-256">
                <Eye className="w-6 h-6 text-emerald-500" data-id="element-257" />
                Wie kan mijn voortgang zien?
              </h3>
              <p className="text-base text-slate-600 mb-6 font-semibold" data-id="element-258">
                Deze mensen kunnen je scores en voortgang bekijken, maar niet je
                wachtwoord of instellingen wijzigen.
              </p>
              <div className="space-y-4" data-id="element-259">
                {linkedInfo.parents.length > 0 && <div data-id="element-260">
                    <div className="flex items-center gap-2 mb-3" data-id="element-261">
                      <Heart className="w-4 h-4 text-rose-500" data-id="element-262" />
                      <span className="text-sm font-bold text-slate-700 uppercase tracking-wide" data-id="element-263">
                        Ouders / Verzorgers
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-264">
                      {linkedInfo.parents.map((p, i) => <div key={i} className="flex items-center gap-3 bg-rose-50 rounded-xl px-4 py-3 border border-rose-100" data-id="element-265">
                          <div className="w-8 h-8 rounded-full bg-rose-200 text-rose-700 flex items-center justify-center text-sm font-bold" data-id="element-266">
                            {p.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-slate-800" data-id="element-267">
                            {p.name}
                          </span>
                        </div>)}
                    </div>
                  </div>}
                {linkedInfo.teachers.length > 0 && <div data-id="element-268">
                    <div className="flex items-center gap-2 mb-3" data-id="element-269">
                      <GraduationCap className="w-4 h-4 text-orange-500" data-id="element-270" />
                      <span className="text-sm font-bold text-slate-700 uppercase tracking-wide" data-id="element-271">
                        Docenten
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-272">
                      {linkedInfo.teachers.map((t, i) => <div key={i} className="flex items-center gap-3 bg-orange-50 rounded-xl px-4 py-3 border border-orange-100" data-id="element-273">
                          <div className="w-8 h-8 rounded-full bg-orange-200 text-orange-700 flex items-center justify-center text-sm font-bold" data-id="element-274">
                            {t.name.charAt(0).toUpperCase()}
                          </div>
                          <div data-id="element-275">
                            <span className="text-sm font-semibold text-slate-800" data-id="element-276">
                              {t.name}
                            </span>
                            {t.className && <span className="text-xs text-slate-500 ml-2" data-id="element-277">
                                ({t.className})
                              </span>}
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </div>
            </Card>
          </motion.div>}

      {/* Starting Period Selector - Only for Learners */}
      {showLearningControls && <motion.div variants={itemVariants} data-id="element-278">
          <Card className="p-8" data-id="element-279">
            <div className="flex justify-between items-start mb-2" data-id="element-280">
              <h3 className="font-black text-slate-900 flex items-center gap-3 text-xl" data-id="element-281">
                <Calendar className="w-6 h-6 text-orange-500" data-id="element-282" />
                Wanneer ben je ingestapt?
              </h3>
              {hasMasteryData && onResetProgress && <Button variant="outline" size="sm" onClick={onResetProgress} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-id="element-283">
                  Volledig resetten
                </Button>}
            </div>
            <p className="text-base text-slate-600 mb-6 font-semibold" data-id="element-284">
              Standaard bepalen we dit automatisch op basis van de datum. Je
              kunt dit hieronder aanpassen.
            </p>

            {hasMasteryData && <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3" data-id="element-285">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" data-id="element-286" />
                <p className="text-sm text-orange-800 font-medium" data-id="element-287">
                  Je bent al begonnen met oefenen, dus je instapniveau staat
                  vast. Wil je toch opnieuw beginnen op een ander niveau?
                  Gebruik dan de reset-knop hierboven (let op: al je voortgang
                  gaat verloren).
                </p>
              </div>}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-288">
              {/* Auto Option */}
              <button disabled={hasMasteryData} onClick={() => onSetStartingPeriod(null)} className={`
                  relative p-5 rounded-xl border-2 transition-all text-center flex flex-col items-center justify-center min-h-[140px]
                  ${!manualPeriod ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-slate-100 bg-white hover:border-orange-200 hover:bg-slate-50'}
                  ${hasMasteryData ? 'opacity-50 cursor-not-allowed hover:border-slate-100 hover:bg-white' : ''}
                `} data-id="element-289">
                {!manualPeriod && <div className="absolute top-3 right-3" data-id="element-290">
                    <Check className="w-5 h-5 text-orange-600" data-id="element-291" />
                  </div>}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${!manualPeriod ? 'bg-orange-200 text-orange-700' : 'bg-slate-100 text-slate-400'}`} data-id="element-292">
                  <Sparkles className="w-6 h-6" data-id="element-293" />
                </div>
                <div className={`font-bold text-base ${!manualPeriod ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-294">
                  Automatisch
                </div>
                <div className="text-xs text-slate-500 mt-2" data-id="element-295">
                  {getAutoPhaseLabel()}
                </div>
              </button>

              {/* Manual Options */}
              {STARTING_PERIODS.map(period => {
            const isActive = manualPeriod === period.id;
            return <button key={period.id} disabled={hasMasteryData} onClick={() => onSetStartingPeriod(period.id)} className={`
                      relative p-5 rounded-xl border-2 transition-all text-center min-h-[140px]
                      ${isActive ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-slate-100 bg-white hover:border-orange-200 hover:bg-slate-50'}
                      ${hasMasteryData ? 'opacity-50 cursor-not-allowed hover:border-slate-100 hover:bg-white' : ''}
                    `} data-id="element-296">
                    {isActive && <div className="absolute top-3 right-3" data-id="element-297">
                        <Check className="w-5 h-5 text-orange-600" data-id="element-298" />
                      </div>}
                    <div className={`text-3xl mb-3 ${isActive ? '' : 'grayscale opacity-60'}`} data-id="element-299">
                      {period.id === 'B' ? '🌱' : period.id === 'M' ? '☀️' : '🎓'}
                    </div>
                    <div className={`font-bold text-base ${isActive ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-300">
                      {period.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-301">
                      {period.description}
                    </div>
                  </button>;
          })}
            </div>

            <div className="mt-6 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-302">
              <p className="text-sm text-slate-700 flex items-center gap-2 font-semibold" data-id="element-303">
                <span className="font-black text-orange-600" data-id="element-304">
                  Huidige start:
                </span>
                Niveau {startingPeriod}
                {selectedGroup}
                {manualPeriod ? ' (Handmatig ingesteld)' : ' (Automatisch bepaald)'}
              </p>
            </div>
          </Card>
        </motion.div>}

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-305">
        <motion.div variants={itemVariants} data-id="element-306">
          <Card className="p-8 h-full" data-id="element-307">
            <h3 className="font-black text-slate-900 mb-6 flex items-center gap-3 text-xl" data-id="element-308">
              <User className="w-6 h-6 text-slate-400" data-id="element-309" />
              Persoonlijke Gegevens
            </h3>
            <div className="space-y-4" data-id="element-310">
              {[{
              label: 'Naam',
              value: displayName
            }, {
              label: 'Rol',
              value: roleLabel
            }, {
              label: 'Schooljaar',
              value: (() => {
                const now = new Date();
                const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
                return `${year} – ${year + 1}`;
              })()
            }].map(row => <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-311">
                  <span className="text-base text-slate-600 font-semibold" data-id="element-312">
                    {row.label}
                  </span>
                  <span className="text-base font-bold text-slate-900" data-id="element-313">
                    {row.value}
                  </span>
                </div>)}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-314">
          <Card className="p-8 h-full" data-id="element-315">
            <h3 className="font-black text-slate-900 mb-6 flex items-center gap-3 text-xl" data-id="element-316">
              <Database className="w-6 h-6 text-slate-400" data-id="element-317" />
              Opslag &amp; Data
            </h3>
            <div className="space-y-4" data-id="element-318">
              {[{
              label: 'Opslagtype',
              value: 'Lokaal (browser)'
            }, ...(showLearningControls ? [{
              label: 'Huidige groep',
              value: `Groep ${selectedGroup}`
            }, {
              label: 'Startpunt',
              value: `${startingPeriod}${selectedGroup}`
            }, {
              label: 'Modus',
              value: manualPeriod ? 'Handmatig' : 'Auto'
            }] : [])].map(row => <div key={row.label} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-319">
                  <span className="text-base text-slate-600 font-semibold" data-id="element-320">
                    {row.label}
                  </span>
                  <span className="text-base font-bold text-slate-900" data-id="element-321">
                    {row.value}
                  </span>
                </div>)}
            </div>
            <p className="text-sm text-slate-500 mt-6 font-semibold" data-id="element-322">
              Je voortgang wordt lokaal in je browser opgeslagen.
            </p>
          </Card>
        </motion.div>
      </div>
    </motion.div>;
}